import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import { openModal } from '../../redux/slices/modals';
import { hapticsImpactLight } from '../../common/haptic';
import ChevronRightIcon from '../../assets/icons/chevron-right.svg?react';
import { ParagraphLargeText } from '../../components/Texts';
import { PHONE_MEDIA_QUERY } from '../../common/responsive';

const SelectedOptionContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: ${({ theme }) => theme.background.light};
  border-radius: 16px;
  cursor: pointer;
  padding-left: 15px;
  padding-right: 15px;
  height: 48px;
`;

const SelectedOptionSymbol = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
`;

const TokenSymbol = styled(ParagraphLargeText)`
  color: ${({ theme }) => theme.text.regular};
  font-size: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 14px;
  }
`;

/**
 * Token select input for the Buy view. Renders a single row (Withdraw-style)
 * showing the selected token; on click opens the Buy token select modal
 * (search + category filters + full token list).
 */
export default function TokenSelectInput({
  value,
  disabled,
  options = [],
  onTokenSelected,
  label,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const selectedOption =
    options.find((opt) => opt.symbol === value || opt.address === value) ||
    options[0];

  const handleOpenModal = () => {
    if (disabled) return;
    hapticsImpactLight();
    dispatch(
      openModal({
        name: 'buySelectToken',
        props: {
          title: label ?? t('selectTokenModal.selectTokenToBuy'),
          onContinue: (token) => {
            onTokenSelected?.(token);
          },
        },
      }),
    );
  };

  return (
    <SelectedOptionContainer
      onClick={handleOpenModal}
      role='button'
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => e.key === 'Enter' && !disabled && handleOpenModal()}
      style={disabled ? { opacity: 0.7, pointerEvents: 'none' } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <SelectedOptionSymbol
          src={selectedOption?.logoURI || 'https://via.placeholder.com/24'}
          alt=''
        />
        <TokenSymbol>
          {selectedOption?.displayLabel || selectedOption?.symbol || t('selectTokenModal.selectToken')}
        </TokenSymbol>
      </div>
      <ChevronRightIcon
        width={12}
        height={12}
        style={{ color: theme.text.title }}
      />
    </SelectedOptionContainer>
  );
}
